import React, { useState, useEffect } from 'react';
import './TariffDetails.css';

const TariffDetails = () => {
  const [selectedPayment, setSelectedPayment] = useState('btc');
  const [otherCryptoOpen, setOtherCryptoOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [contractData, setContractData] = useState(null);

  useEffect(() => {
    // Parse card data from URL
    const pathParts = window.location.pathname.split('/');
    const encodedData = pathParts[pathParts.length - 1];
    
    const calculateAndSetData = () => {
      try {
        const cardData = JSON.parse(decodeURIComponent(encodedData));
        
        // Calculate contract details based on card data
        const calculations = calculateContractDetails(cardData);
        setContractData(calculations);
      } catch (error) {
        // Fallback to default data if parsing fails
        console.error('Error parsing card data:', error);
        setContractData(getDefaultContractData());
      }
    };
    
    calculateAndSetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateContractDetails = (card) => {
    const price = parseFloat(card.price);
    const power = parseFloat(card.power.replace(' TH/s', ''));
    const durationMonths = parseInt(card.duration.split(' ')[0]);
    const durationDays = Math.round(durationMonths * 30.5);
    
    // Constants for calculations
    const btcPriceReference = 100000; // $100k BTC reference
    const dailyBtcPerTH = 0.00000041; // FPPS rate
    const serviceFeePerTHPerDay = 0.03098; // From reference
    
    // Calculate mining output
    const totalBtcMined = power * dailyBtcPerTH * durationDays;
    const totalMiningRewardUSD = totalBtcMined * btcPriceReference;
    
    // Calculate service fees based on package type
    let serviceFeeTotal = 0;
    let serviceFeeDescription = '';
    let prepaidMonths = 0;
    
    if (card.type === 'basic') {
      // Start package - service fee deducted from rewards
      serviceFeeTotal = power * serviceFeePerTHPerDay * durationDays;
      serviceFeeDescription = 'Deducted from mining rewards';
      prepaidMonths = 0;
    } else if (card.type === 'medium') {
      // Prepaid - one month paid in advance
      serviceFeeTotal = power * serviceFeePerTHPerDay * 30.5;
      serviceFeeDescription = '1 month prepaid';
      prepaidMonths = 1;
    } else if (card.type === 'premium') {
      // Fully prepaid - all service fees paid in advance
      serviceFeeTotal = power * serviceFeePerTHPerDay * durationDays;
      serviceFeeDescription = 'Fully prepaid';
      prepaidMonths = durationMonths;
    }
    
    // Calculate processing fee (2.5% for card payments, 0 for crypto)
    const basePrice = card.type === 'premium' ? price - serviceFeeTotal : price;
    const processingFeePercent = selectedPayment === 'card' ? 2.5 : 0;
    const processingFee = (price * processingFeePercent) / 100;
    
    // Calculate totals
    const contractPrice = card.type === 'premium' ? basePrice : price;
    const totalPayment = price + processingFee;
    
    // Calculate net profit
    const netProfit = card.type === 'basic' 
      ? totalMiningRewardUSD - price - serviceFeeTotal
      : totalMiningRewardUSD - totalPayment;
    
    // Determine ASIC model based on power and duration
    let asicModel = 'Antminer S21 Pro 234 TH/s';
    if (power > 180) {
      asicModel = 'Antminer S21 Pro 245 TH/s';
    } else if (power > 150) {
      asicModel = 'Antminer S21 200 TH/s';
    } else if (power > 100) {
      asicModel = 'Antminer S19j Pro 110 TH/s';
    } else if (power < 50) {
      asicModel = 'Antminer S19 95 TH/s';
    }
    
    return {
      packageTitle: card.title,
      packageSubtitle: card.subtitle,
      packageType: card.type,
      totalPower: `${power.toFixed(2)} TH/s`,
      duration: `${durationDays} days (${durationMonths} months)`,
      asicModel: asicModel,
      contractPrice: formatCurrency(contractPrice),
      serviceFee: formatCurrency(serviceFeePerTHPerDay),
      serviceFeeTotal: formatCurrency(serviceFeeTotal),
      serviceFeeDescription: serviceFeeDescription,
      processingFee: formatCurrency(processingFee),
      totalPayment: formatCurrency(totalPayment),
      totalPaymentRaw: totalPayment,
      
      // Additional calculation details
      durationDays: durationDays,
      durationMonths: durationMonths,
      totalBtcMined: totalBtcMined.toFixed(8),
      totalMiningRewardUSD: formatCurrency(totalMiningRewardUSD),
      netProfit: formatCurrency(netProfit),
      roiPercent: ((netProfit / totalPayment) * 100).toFixed(1),
      prepaidMonths: prepaidMonths,
      
      // Daily metrics
      dailyBtc: (power * dailyBtcPerTH).toFixed(8),
      dailyUSD: formatCurrency(power * dailyBtcPerTH * btcPriceReference),
      dailyServiceFee: formatCurrency(power * serviceFeePerTHPerDay),
      dailyNetProfit: formatCurrency((power * dailyBtcPerTH * btcPriceReference) - (power * serviceFeePerTHPerDay))
    };
  };

  const getDefaultContractData = () => {
    return {
      packageTitle: 'Start Package',
      packageSubtitle: 'Service fee deducted from mining reward',
      packageType: 'basic',
      totalPower: '96.49 TH/s',
      duration: '92 days (3 months)',
      asicModel: 'Antminer S19j Pro 110 TH/s',
      contractPrice: '$49.00',
      serviceFee: '$0.03098',
      serviceFeeTotal: '$275.00',
      serviceFeeDescription: 'Deducted from mining rewards',
      processingFee: '$0.00',
      totalPayment: '$49.00',
      totalPaymentRaw: 49,
      durationDays: 92,
      durationMonths: 3,
      totalBtcMined: '0.00363848',
      totalMiningRewardUSD: '$363.85',
      netProfit: '$39.85',
      roiPercent: '81.3',
      prepaidMonths: 0,
      dailyBtc: '0.00003956',
      dailyUSD: '$3.96',
      dailyServiceFee: '$2.99',
      dailyNetProfit: '$0.97'
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handlePaymentSelect = (method) => {
    setSelectedPayment(method);
    if (method !== 'other-crypto') {
      setOtherCryptoOpen(false);
    }
  };

  const toggleOtherCrypto = () => {
    setOtherCryptoOpen(!otherCryptoOpen);
    if (!otherCryptoOpen) {
      setSelectedPayment('other-crypto');
    }
  };

  const handleCryptoSelect = (crypto) => {
    setSelectedPayment(crypto);
    setOtherCryptoOpen(false);
  };

  const handleBuyNow = () => {
    if (!agreedToTerms) {
      alert('Please agree to the terms and conditions');
      return;
    }
    // Handle purchase logic here
    console.log('Processing purchase with payment method:', selectedPayment);
    alert(`Processing purchase of ${contractData?.totalPayment} via ${selectedPayment.toUpperCase()}`);
  };

  const handleBack = () => {
    window.history.pushState({ page: 'promo' }, '', '/mining-store/promo');
    window.location.reload();
  };

  if (!contractData) {
    return <div className="tariff__content" style={{ padding: '100px 24px', textAlign: 'center' }}>Loading contract details...</div>;
  }

  return (
    <div className="tariff__content">
      {/* Back Button */}
      <button 
        className="ui-back tariff__back-button--desktop" 
        type="button"
        onClick={handleBack}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15.8333 9.99996H4.16663M4.16663 9.99996L9.99996 15.8333M4.16663 9.99996L9.99996 4.16663" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
        </svg>
        <p className="ui-back__button">Back</p>
      </button>
      
      <div className="tariff__card">
        <div className="tariff__card-block">
          <div className="tariff__wrapper">
            {/* Left Section - Contract Info */}
            <div className="tariff__info">
              <div className="tariff__info-details">
                <p className="tariff__info-title">Contract</p>
                
                <div className="tariff__image-container">
                  <div className="tariff__image-block">
                    <picture className="tariff__cover-image">
                      <source srcSet="/images/order/asic.webp" type="image/webp" />
                      <img src="/images/order/asic.png" alt="ASIC banner" loading="lazy" />
                    </picture>
                  </div>
                </div>

                <div className="tariff__info-data">
                  <h2 className="tariff__info-data-title">{contractData.packageTitle}</h2>
                  <p className="tariff__package-subtitle">{contractData.packageSubtitle}</p>
                  
                  <div className="tariff__line line">
                    <div className="text_group textborder">
                      <p className="text_title">Total mining power</p>
                      <p className="text_value">{contractData.totalPower}</p>
                    </div>
                    <div className="text_group">
                      <p className="text_title">Duration</p>
                      <p className="text_value">{contractData.duration}</p>
                    </div>
                  </div>

                  <div className="text_group">
                    <p className="text_title">ASIC Model:</p>
                    <p className="text_value">{contractData.asicModel}</p>
                  </div>
                  
                  {/* Mining Performance Metrics */}
                  <div className="tariff__metrics">
                    <h3 className="tariff__metrics-title">Expected Mining Output</h3>
                    <div className="tariff__metric-row">
                      <span className="tariff__metric-label">Total BTC Mined:</span>
                      <span className="tariff__metric-value">{contractData.totalBtcMined} BTC</span>
                    </div>
                    <div className="tariff__metric-row">
                      <span className="tariff__metric-label">USD Value (@ $100k):</span>
                      <span className="tariff__metric-value">{contractData.totalMiningRewardUSD}</span>
                    </div>
                    <div className="tariff__metric-row">
                      <span className="tariff__metric-label">Daily Mining:</span>
                      <span className="tariff__metric-value">{contractData.dailyBtc} BTC / {contractData.dailyUSD}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Payment Details */}
            <div className="tariff__image-section">
              <div className="tariff__order-details">
                <div className="tariff__detail-item">
                  <p className="tariff__detail-title" style={{ fontSize: '16px', fontWeight: '600', lineHeight: '24px' }}>
                    Payment Details
                  </p>
                  <p className="tariff__detail-value"></p>
                </div>

                <div className="tariff__detail-item">
                  <p className="tariff__detail-title">Contract Price</p>
                  <p className="tariff__detail-value">{contractData.contractPrice}</p>
                </div>

                <div className="tariff__detail-item">
                  <p className="tariff__detail-title">
                    Service fee per TH/day
                    <div className="tooltip tariff__tooltip-icon">
                      <div className="tooltip-text" style={{ display: 'none' }}>
                        Service fee is charged for electricity costs, per 1 TH/s per day. {contractData.serviceFeeDescription}
                      </div>
                      <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <g clipPath="url(#clip0_264_44934)">
                          <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                        </g>
                        <defs>
                          <clipPath id="clip0_264_44934">
                            <rect width="18" height="18" fill="white"></rect>
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  </p>
                  <p className="tariff__detail-value">{contractData.serviceFee}</p>
                </div>
                
                <div className="tariff__detail-item">
                  <p className="tariff__detail-title">Total Service Fee ({contractData.durationDays} days)</p>
                  <p className="tariff__detail-value">{contractData.serviceFeeTotal}</p>
                </div>

                <div className="tariff__detail-item">
                  <p className="tariff__detail-title">
                    Card Processing Fee
                    <div className="tooltip tariff__tooltip-icon">
                      <div className="tooltip-text" style={{ display: 'none' }}>
                        2.5% from total amount charged by the payment processor for transaction made via card
                      </div>
                      <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <g clipPath="url(#clip0_264_44934)">
                          <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                        </g>
                        <defs>
                          <clipPath id="clip0_264_44934">
                            <rect width="18" height="18" fill="white"></rect>
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  </p>
                  <p className="tariff__detail-value">{contractData.processingFee}</p>
                </div>

                <div className="tariff__detail-item">
                  <p className="tariff__detail-title" style={{ fontSize: '16px', fontWeight: '600', lineHeight: '24px' }}>
                    Total Payment
                  </p>
                  <p className="tariff__detail-value" style={{ fontSize: '20px', color: '#1570ef' }}>{contractData.totalPayment}</p>
                </div>
                
                {/* Profitability Summary */}
                <div className="tariff__profitability">
                  <div className="tariff__profit-item tariff__profit-item--positive">
                    <p className="tariff__profit-label">Expected Net Profit</p>
                    <p className="tariff__profit-value">{contractData.netProfit}</p>
                  </div>
                  <div className="tariff__profit-item">
                    <p className="tariff__profit-label">ROI</p>
                    <p className="tariff__profit-value">{contractData.roiPercent}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="tariff__additional-info">
          <div className="disclaimer tariff__disclaimer">
            <p className="disclaimer__title">Disclaimer</p>
            <p className="disclaimer__description">
              These estimates are based on current Bitcoin network conditions (FPPS rate: 0.00000041 BTC/TH/day) and a BTC reference price of $100,000. 
              Actual mining results may vary significantly due to:
              • Bitcoin price volatility
              • Changes in network difficulty
              • Mining pool performance variations
              • Network hash rate fluctuations
              {contractData.packageType === 'basic' && ' • Service fees are automatically deducted from your daily mining rewards'}
              {contractData.packageType === 'medium' && ' • One month of service fees is prepaid, remaining fees deducted from rewards'}
              {contractData.packageType === 'premium' && ' • All service fees are fully prepaid for the entire contract period'}
              <br /><br />
              Daily service fees: {contractData.dailyServiceFee} ({contractData.totalPower} × $0.03098/TH/day).
              This information is for estimation purposes only and does not constitute financial advice or guarantee of returns.
            </p>
          </div>

          {/* Payment Options Section */}
          <div className="tariff__payment-options">
            <div className="container-payment">
              <div className="payment-container tariff_page_old">
                <div className="payment-container__form-payment">
                  <div className="payment-container__payment-title">Payment Options:</div>
                  
                  <div className="payment-container__form-payment-block">
                    <div className="payment-container__form-payment-list-block">
                      <div className="payment-container__form-payment-list">
                        {/* Card Payment Button */}
                        <button
                          className={`ui-payment-method-button ${selectedPayment === 'card' ? 'active' : ''}`}
                          type="button"
                          onClick={() => handlePaymentSelect('card')}
                        >
                          <svg width="92" height="32" viewBox="0 0 92 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="ui-payment-method-button__icon">
                            <mask id="path-1-inside-1_0_4595" fill="white">
                              <path d="M0 0H46V32H0V0Z"></path>
                            </mask>
                            <path d="M0 0H46V32H0V0Z" fill="white"></path>
                            <path d="M46 0H45V32H46H47V0H46Z" fill="#EAECF0" mask="url(#path-1-inside-1_0_4595)"></path>
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.3341 21.1443H11.5877L9.52833 13.0565C9.43058 12.6844 9.22304 12.3555 8.91774 12.2005C8.15584 11.811 7.31628 11.5009 6.40039 11.3446V11.0332H10.8245C11.4351 11.0332 11.893 11.5009 11.9694 12.0442L13.0379 17.8782L15.7829 11.0332H18.4529L14.3341 21.1443ZM19.9794 21.1443H17.3857L19.5214 11.0332H22.1151L19.9794 21.1443ZM25.4707 13.8343C25.547 13.2897 26.0049 12.9783 26.5392 12.9783C27.3788 12.9001 28.2933 13.0565 29.0565 13.4447L29.5145 11.2677C28.7512 10.9564 27.9117 10.8 27.1498 10.8C24.6324 10.8 22.8007 12.2005 22.8007 14.1443C22.8007 15.623 24.0982 16.3995 25.0141 16.8672C26.0049 17.3336 26.3865 17.645 26.3102 18.1114C26.3102 18.811 25.547 19.1224 24.7851 19.1224C23.8692 19.1224 22.9533 18.8892 22.1151 18.4996L21.6571 20.6779C22.573 21.0661 23.5639 21.2225 24.4798 21.2225C27.3024 21.2993 29.0565 19.9001 29.0565 17.8C29.0565 15.1553 25.4707 15.0003 25.4707 13.8343V13.8343ZM38.134 21.1443L36.0746 11.0332H33.8625C33.4046 11.0332 32.9466 11.3446 32.794 11.811L28.9805 21.1443H31.6505L32.1834 19.667H35.464L35.7693 21.1443H38.134ZM34.2439 13.7561L35.0058 17.5668H32.8701L34.2439 13.7561Z" fill="#172B85"></path>
                            <rect width="46" height="32" transform="translate(46)" fill="white"></rect>
                          </svg>
                          <span className="ui-payment-method-button__text">Card</span>
                        </button>

                        {/* Bank Transfer Button */}
                        <button
                          className={`ui-payment-method-button ${selectedPayment === 'bank' ? 'active' : ''}`}
                          type="button"
                          onClick={() => handlePaymentSelect('bank')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="ui-payment-method-button__icon">
                            <g style={{ mixBlendMode: 'multiply' }}>
                              <path d="M5 9.00002V17M9.5 9.00002V17M14.5 9.00002V17M19 9.00002V17M3 18.6L3 19.4C3 19.9601 3 20.2401 3.10899 20.454C3.20487 20.6422 3.35785 20.7952 3.54601 20.891C3.75992 21 4.03995 21 4.6 21H19.4C19.9601 21 20.2401 21 20.454 20.891C20.6422 20.7952 20.7951 20.6422 20.891 20.454C21 20.2401 21 19.9601 21 19.4V18.6C21 18.04 21 17.7599 20.891 17.546C20.7951 17.3579 20.6422 17.2049 20.454 17.109C20.2401 17 19.9601 17 19.4 17H4.6C4.03995 17 3.75992 17 3.54601 17.109C3.35785 17.2049 3.20487 17.3579 3.10899 17.546C3 17.7599 3 18.04 3 18.6ZM11.6529 3.07715L4.25291 4.7216C3.80585 4.82094 3.58232 4.87062 3.41546 4.99082C3.26829 5.09685 3.15273 5.24092 3.08115 5.40759C3 5.59654 3 5.82553 3 6.28349L3 7.40002C3 7.96007 3 8.2401 3.10899 8.45401C3.20487 8.64217 3.35785 8.79515 3.54601 8.89103C3.75992 9.00002 4.03995 9.00002 4.6 9.00002H19.4C19.9601 9.00002 20.2401 9.00002 20.454 8.89103C20.6422 8.79515 20.7951 8.64217 20.891 8.45401C21 8.2401 21 7.96007 21 7.40002V6.2835C21 5.82553 21 5.59655 20.9188 5.40759C20.8473 5.24092 20.7317 5.09685 20.5845 4.99082C20.4177 4.87062 20.1942 4.82094 19.7471 4.7216L12.3471 3.07715C12.2176 3.04837 12.1528 3.03398 12.0874 3.02824C12.0292 3.02314 11.9708 3.02314 11.9126 3.02824C11.8472 3.03398 11.7824 3.04837 11.6529 3.07715Z" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                            </g>
                          </svg>
                          <span className="ui-payment-method-button__text">Bank Transfer</span>
                        </button>

                        {/* BTC Button */}
                        <button
                          className={`ui-payment-method-button ${selectedPayment === 'btc' ? 'active' : ''}`}
                          type="button"
                          onClick={() => handlePaymentSelect('btc')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="ui-payment-method-button__icon">
                            <g clipPath="url(#clip0_272_22442)">
                              <path d="M24 12C24 14.3734 23.2962 16.6935 21.9776 18.6668C20.6591 20.6402 18.7849 22.1783 16.5922 23.0866C14.3995 23.9948 11.9867 24.2324 9.65892 23.7694C7.33115 23.3064 5.19295 22.1635 3.51472 20.4853C1.83649 18.8071 0.693605 16.6689 0.230582 14.3411C-0.232441 12.0133 0.00519936 9.60051 0.913451 7.4078C1.8217 5.21509 3.35977 3.34094 5.33316 2.02236C7.30655 0.703788 9.62663 0 12 0C15.1826 0 18.2348 1.26428 20.4853 3.51472C22.7357 5.76516 24 8.8174 24 12Z" fill="#F7931A"></path>
                              <path fillRule="evenodd" clipRule="evenodd" d="M6.713 6.60268L9.568 6.60229L9.56874 4.22359L10.9975 4.22813L10.9954 6.50894L12.16 6.50973L12.157 4.20545L13.6092 4.20484L13.6048 6.52353C13.6048 6.52353 15.9489 6.42059 16.9517 8.08089C17.9545 9.74119 16.5429 11.1325 16.0383 11.3061C16.0383 11.3061 18.262 11.8498 18.1565 14.1199C18.051 16.39 16.107 17.3119 13.8279 17.4087L13.8449 19.8075L12.3927 19.8081L12.3966 17.441L11.2432 17.436L11.2436 19.8189L9.80222 19.8178L9.80571 17.4451L6.87362 17.4402L7.15279 15.7255C7.15279 15.7255 7.9806 15.7296 8.29112 15.7209C8.60164 15.7123 8.70789 15.3585 8.70876 15.033C8.70964 14.7075 8.60013 9.34629 8.60613 8.90083C8.61213 8.45537 8.47804 8.14458 7.95283 8.14865C7.42762 8.15272 6.70619 8.13257 6.70619 8.13257L6.713 6.60268ZM11.1848 12.5434L11.2329 15.6736C11.2329 15.6736 15.2442 16.0139 15.2143 14.0308C15.1844 12.0477 11.1848 12.5434 11.1848 12.5434ZM11.1514 11.0615L11.1553 8.18052C11.1553 8.18052 14.4372 7.90197 14.4522 9.50056C14.4672 11.0992 12.4674 11.0206 11.1514 11.0615Z" fill="white"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_272_22442">
                                <rect width="24" height="24" fill="white"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                          <span className="ui-payment-method-button__text">BTC</span>
                        </button>

                        {/* Other Crypto Dropdown Button */}
                        <button
                          className={`ui-payment-method-button ${selectedPayment === 'other-crypto' || otherCryptoOpen ? 'active' : ''}`}
                          type="button"
                          onClick={toggleOtherCrypto}
                        >
                          <span className="ui-payment-method-button__text">
                            <span className="payment-container__arrow">
                              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                <path d="M4.5 6L8.5 10L12.5 6" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                              </svg>
                            </span>
                            Other Crypto
                          </span>
                        </button>

                        {/* Crypto Dropdown */}
                        {otherCryptoOpen && (
                          <div className="ui-payment-dropdown-content">
                            <button
                              className="ui-payment-dropdown-item"
                              onClick={() => handleCryptoSelect('eth')}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#627EEA"/>
                                <path d="M12.373 3V9.652L17.996 12.219L12.373 3Z" fill="white" fillOpacity="0.602"/>
                                <path d="M12.373 3L6.75 12.219L12.373 9.652V3Z" fill="white"/>
                                <path d="M12.373 16.476V20.996L18 13.212L12.373 16.476Z" fill="white" fillOpacity="0.602"/>
                                <path d="M12.373 20.996V16.475L6.75 13.212L12.373 20.996Z" fill="white"/>
                                <path d="M12.373 15.483L17.996 12.219L12.373 9.654V15.483Z" fill="white" fillOpacity="0.2"/>
                                <path d="M6.75 12.219L12.373 15.483V9.654L6.75 12.219Z" fill="white" fillOpacity="0.602"/>
                              </svg>
                              <span>Ethereum (ETH)</span>
                            </button>
                            <button
                              className="ui-payment-dropdown-item"
                              onClick={() => handleCryptoSelect('usdt')}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#26A17B"/>
                                <path d="M13.191 12.826C13.0918 12.8385 12.5508 12.8822 11.9988 12.8822C11.5547 12.8822 10.9402 12.8447 10.8066 12.826C8.23027 12.701 6.29688 12.1787 6.29688 11.5517C6.29688 10.9247 8.23027 10.396 10.8066 10.2773V12.3823C10.9465 12.3948 11.5672 12.4385 11.9988 12.4385C12.5195 12.4385 13.0605 12.401 13.191 12.3823V10.2773C15.7611 10.396 17.7008 10.9185 17.7008 11.5517C17.7008 12.185 15.7611 12.701 13.191 12.826ZM13.191 10.146V8.21104H17.0977V6.01953H6.89453V8.21104H10.8066V10.146C7.83398 10.2898 5.625 11.0105 5.625 11.8755C5.625 12.7405 7.83398 13.4612 10.8066 13.6113V18.5801H13.191V13.6113C16.1574 13.4612 18.3664 12.7405 18.3664 11.8755C18.3664 11.0105 16.1574 10.2898 13.191 10.146Z" fill="white"/>
                              </svg>
                              <span>Tether (USDT)</span>
                            </button>
                            <button
                              className="ui-payment-dropdown-item"
                              onClick={() => handleCryptoSelect('usdc')}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#2775CA"/>
                                <path d="M15.3 14.8C15.3 13.1 14.2 12.5 11.9 12.3C10.3 12.1 10 11.7 10 10.9C10 10.1 10.5 9.7 11.6 9.7C12.6 9.7 13.1 10 13.4 10.8C13.4 10.9 13.5 11 13.6 11H14.8C15 11 15.1 10.9 15.1 10.7V10.7C14.9 9.4 13.9 8.5 12.5 8.4V6.9C12.5 6.7 12.4 6.6 12.2 6.6H11.5C11.3 6.6 11.2 6.7 11.2 6.9V8.3C9.4 8.5 8.4 9.6 8.4 11C8.4 12.6 9.5 13.3 11.8 13.5C13.3 13.7 13.7 14 13.7 14.9C13.7 15.8 13 16.3 12 16.3C10.7 16.3 10.2 15.8 10 15C10 14.8 9.9 14.7 9.7 14.7H8.4C8.2 14.7 8.1 14.8 8.1 15C8.3 16.5 9.4 17.5 11.2 17.6V19.1C11.2 19.3 11.3 19.4 11.5 19.4H12.2C12.4 19.4 12.5 19.3 12.5 19.1V17.6C14.3 17.3 15.3 16.2 15.3 14.8Z" fill="white"/>
                              </svg>
                              <span>USD Coin (USDC)</span>
                            </button>
                            <button
                              className="ui-payment-dropdown-item"
                              onClick={() => handleCryptoSelect('bnb')}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#F3BA2F"/>
                                <path d="M9.375 10.5L12 7.875L14.625 10.5L16.5 8.625L12 4.125L7.5 8.625L9.375 10.5ZM4.125 12L6 10.125L7.875 12L6 13.875L4.125 12ZM9.375 13.5L12 16.125L14.625 13.5L16.5 15.375L12 19.875L7.5 15.375L9.375 13.5ZM16.125 12L18 10.125L19.875 12L18 13.875L16.125 12Z" fill="white"/>
                                <path d="M13.1836 11.9925H13.1911L13.1985 12L13.1911 12.0075H13.1836L12 13.1911L10.8089 12L12 10.8089L13.1836 11.9925Z" fill="white"/>
                              </svg>
                              <span>Binance Coin (BNB)</span>
                            </button>
                            <button
                              className="ui-payment-dropdown-item"
                              onClick={() => handleCryptoSelect('sol')}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="24" height="24" rx="12" fill="url(#paint0_linear_sol)"/>
                                <path d="M7.75 14.9286C7.83333 14.8452 7.94167 14.8036 8.075 14.8036H18.5C18.7083 14.8036 18.8333 15.0536 18.7 15.2202L16.25 18.0714C16.1667 18.1548 16.0583 18.1964 15.925 18.1964H5.5C5.29167 18.1964 5.16667 17.9464 5.3 17.7798L7.75 14.9286ZM7.75 5.92857C7.83333 5.84524 7.94167 5.80357 8.075 5.80357H18.5C18.7083 5.80357 18.8333 6.05357 18.7 6.22024L16.25 9.07143C16.1667 9.15476 16.0583 9.19643 15.925 9.19643H5.5C5.29167 9.19643 5.16667 8.94643 5.3 8.77976L7.75 5.92857ZM16.25 10.4286C16.1667 10.3452 16.0583 10.3036 15.925 10.3036H5.5C5.29167 10.3036 5.16667 10.5536 5.3 10.7202L7.75 13.5714C7.83333 13.6548 7.94167 13.6964 8.075 13.6964H18.5C18.7083 13.6964 18.8333 13.4464 18.7 13.2798L16.25 10.4286Z" fill="white"/>
                                <defs>
                                  <linearGradient id="paint0_linear_sol" x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#00FFA3"/>
                                    <stop offset="1" stopColor="#DC1FFF"/>
                                  </linearGradient>
                                </defs>
                              </svg>
                              <span>Solana (SOL)</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms and Buy Button */}
                <div className="payment-container__button-block">
                  <div className="payment-container__checked-privacy_policy">
                    <label className="ui-checkbox payment-container__form-confirm-checkbox" tabIndex="0">
                      <input
                        type="checkbox"
                        className="ui-checkbox__input"
                        name="checked_privacy_policy"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                      />
                      <span className="ui-checkbox__visual"></span>
                      <span className="ui-checkbox__wrap">
                        <span className="ui-checkbox__text">
                          <div className="checked_box">
                            <span className="payment-container__text">I agree to your</span>
                            <a className="payment-container__link" href="#" onClick={(e) => e.preventDefault()}>Refund Policy</a>
                            <span className="payment-container__text">and</span>
                            <a className="payment-container__link" href="#" onClick={(e) => e.preventDefault()}>Terms of use</a>
                          </div>
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="payment-container____button-block">
                    <div style={{ position: 'relative' }}>
                      <button
                        className={`orange ${!agreedToTerms ? 'disabled' : ''}`}
                        type="button"
                        disabled={!agreedToTerms}
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TariffDetails;
